// ChatDock.js — Gestionnaire de fenêtres de chat DM FLOTTANTES, monté app-wide.
// Permet de discuter avec un ami en instantané SANS aller dans l'espace membre : la languette « Amis en
// ligne » (et « Mes messages ») appelle openChatWindow(user) → une petite fenêtre s'ouvre ici, discrète,
// réductible (bouton −) et refermable (bouton ×).
// Optimisation espace : DESKTOP = jusqu'à 3 fenêtres côte à côte (en bas à gauche, pour ne pas gêner les
// languettes de droite) ; MOBILE = 1 SEULE à la fois (écran trop réduit) et pleine largeur.
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiX, FiMessageCircle } from 'react-icons/fi';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';
import DirectMessage from './DirectMessage';
import { onOpenChatWindow } from '../services/chatWindows';

const MOBILE_Q = '(max-width: 767px)';
const isMobileNow = () => typeof window !== 'undefined' && window.matchMedia(MOBILE_Q).matches;

const Wrap = styled.div`
  position: fixed; z-index: 1600; bottom: 1rem; left: 1rem;
  display: flex; align-items: flex-end; gap: .6rem; pointer-events: none;
  @media (max-width: 767px){ left: .5rem; right: .5rem; bottom: .8rem; }
`;
const Win = styled.div`
  pointer-events: auto; width: 300px; max-width: calc(100vw - 1.5rem);
  box-shadow: ${theme.shadows.card}; border-radius: ${theme.radii.md}; background: ${theme.colors.bgCard};
  @media (min-width: 768px) and (max-width: 1099px){ width: 264px; }
  @media (max-width: 767px){ width: 100%; }
`;
const CollapsedBar = styled.button`
  pointer-events: auto; display: inline-flex; align-items: center; gap: .5rem;
  background: ${theme.colors.gradient}; color: #fff; border: none; border-radius: 999px;
  padding: .5rem .9rem; font-weight: 700; font-size: .82rem; cursor: pointer;
  box-shadow: ${theme.shadows.card}; max-width: 220px;
  .nm { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .x { display: inline-flex; opacity: .85; } .x:hover { opacity: 1; }
`;

export default function ChatDock() {
  const { isLoggedIn } = useAuth();
  const [wins, setWins] = useState([]);   // [{ target, collapsed }]
  const [isMobile, setIsMobile] = useState(isMobileNow);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_Q);
    const h = () => setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', h); else mq.addListener(h);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', h); else mq.removeListener(h); };
  }, []);

  // Ouverture d'une fenêtre (depuis n'importe où via openChatWindow).
  useEffect(() => onOpenChatWindow((target) => {
    setWins((prev) => {
      const max = isMobileNow() ? 1 : 3;
      const exists = prev.find((w) => w.target.id === target.id);
      // Déjà ouverte → la dé-réduire + la ramener en avant (fin de liste).
      let next = exists
        ? [...prev.filter((w) => w.target.id !== target.id), { target: exists.target, collapsed: false }]
        : [...prev, { target, collapsed: false }];
      // Plafond : au-delà du max on retire les plus anciennes (mobile → 1 seule).
      if (next.length > max) next = next.slice(next.length - max);
      return next;
    });
  }), []);

  useEffect(() => { if (!isLoggedIn) setWins([]); }, [isLoggedIn]);
  // Passage desktop → mobile : on ne garde qu'une seule fenêtre.
  useEffect(() => { if (isMobile) setWins((p) => (p.length > 1 ? p.slice(-1) : p)); }, [isMobile]);

  const close = useCallback((id) => setWins((p) => p.filter((w) => w.target.id !== id)), []);
  const setCollapsed = useCallback((id, v) => setWins((p) => p.map((w) => (w.target.id === id ? { ...w, collapsed: v } : w))), []);

  if (!isLoggedIn || wins.length === 0) return null;

  return (
    <Wrap>
      {wins.map(({ target, collapsed }) => (collapsed ? (
        <CollapsedBar key={target.id} onClick={() => setCollapsed(target.id, false)} title="Ouvrir la discussion">
          <FiMessageCircle />
          <span className="nm">{target.display_name || target.username}</span>
          <span className="x" role="button" aria-label="Fermer la discussion" onClick={(e) => { e.stopPropagation(); close(target.id); }}><FiX /></span>
        </CollapsedBar>
      ) : (
        <Win key={target.id}>
          <DirectMessage target={target} onClose={() => close(target.id)} onMinimize={() => setCollapsed(target.id, true)} />
        </Win>
      )))}
    </Wrap>
  );
}
